import React from 'react'
import { MutationFn } from 'react-apollo'
import { Select } from '@deal/components'
import styles from './styles.css'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import {
  ExpertCuratedVariationOptionFragment,
  SelectExperienceVariationOptionComponent,
  SelectExperienceVariationOptionMutation,
  SelectExperienceVariationOptionMutationVariables
} from '#src/generated/graphql'

interface DurationProps {
  expertCuratedOrderId: string
  variationOptions: ExpertCuratedVariationOptionFragment[]
}

interface DurationState {
  durationType: DurationType
}

type DurationType = 'hours' | 'days'

class Duration extends React.Component<DurationProps & WithAnalyticsProps, DurationState> {
  constructor(props: DurationProps) {
    super(props)

    this.state = { durationType: 'hours' }

    this.handleDurationTypeChange = this.handleDurationTypeChange.bind(this)
    this.getValue = this.getValue.bind(this)
  }

  // When the duration type changes we need to find a default charter length that matches the new duration type
  public handleDurationTypeChange(
    durationType: DurationType,
    selectExpertCuratedItemVariation: MutationFn<
      SelectExperienceVariationOptionMutation,
      SelectExperienceVariationOptionMutationVariables
    >
  ) {
    const enabledOptions = this.props.variationOptions.filter(option => option.enabled)

    if (durationType === 'hours') {
      // Find first variation that is less than a day
      const defaultHour = enabledOptions.find(
        option => this.getValue(option.sellable.variants[0].value) < 24
      )
      const defaultHourId = defaultHour && defaultHour.sellable.id
      defaultHourId &&
        selectExpertCuratedItemVariation({
          variables: {
            input: { id: this.props.expertCuratedOrderId, sellableId: defaultHourId }
          }
        })
    } else {
      // Find first variation that is greater than a day
      const defaultDay = enabledOptions.find(
        option => this.getValue(option.sellable.variants[0].value) > 24
      )
      const defaultDayId = defaultDay && defaultDay.sellable.id
      defaultDayId &&
        selectExpertCuratedItemVariation({
          variables: {
            input: { id: this.props.expertCuratedOrderId, sellableId: defaultDayId }
          }
        })
    }
  }

  public getValue(value: string) {
    return parseInt(value.replace(/[^0-9]/, ''), 10)
  }

  render() {
    const { variationOptions, expertCuratedOrderId, analytics } = this.props
    const enabledOptions = variationOptions.filter(option => option.enabled)

    const dayOptions = enabledOptions
      .filter(option => this.getValue(option.sellable.variants[0].value) > 24)
      .map(option => ({
        value: option.sellable.id,
        label: option.sellable.variants[0].displayValueHtml
      }))

    const hourOptions = enabledOptions
      .filter(option => this.getValue(option.sellable.variants[0].value) < 24)
      .map(option => ({
        value: option.sellable.id,
        label: option.sellable.variants[0].displayValueHtml
      }))

    const selectedSellable = enabledOptions.find(option => option.selected)

    const charterType =
      parseInt(selectedSellable!.sellable.variants[0].value, 10) > 24 ? 'days' : 'hours'

    return (
      <SelectExperienceVariationOptionComponent>
        {(selectExpertCuratedItemVariation, { loading }) => {
          return (
            <div className={styles.duration}>
              <div className={styles.durationWrapper}>
                <div className={styles.durationTitle}>Book your charter</div>
                <div className={styles.durationSubtitle}>
                  How long would you like to charter for?
                </div>

                <div className={styles.selectors}>
                  <Select
                    label="Pick your itinerary"
                    disabled={loading}
                    onChange={e =>
                      this.handleDurationTypeChange(
                        e.target.value as DurationType,
                        selectExpertCuratedItemVariation
                      )
                    }
                    value={charterType}
                    options={[
                      { label: 'Per Hour', value: 'hours', disabled: !hourOptions.length },
                      { label: 'Per Day', value: 'days', disabled: !dayOptions.length }
                    ]}
                  />
                  <Select
                    label="duration"
                    value={selectedSellable && selectedSellable.sellable.id}
                    disabled={loading}
                    labelHidden={true}
                    options={charterType === 'hours' ? hourOptions : dayOptions}
                    onChange={e => {
                      analytics &&
                        analytics.expertCuratedItemVariationSelected({
                          expert_curated_order_id: expertCuratedOrderId,
                          sellable_id: e.target.value
                        })

                      selectExpertCuratedItemVariation({
                        variables: {
                          input: { id: expertCuratedOrderId, sellableId: e.target.value }
                        }
                      })
                    }}
                  />
                </div>
              </div>
            </div>
          )
        }}
      </SelectExperienceVariationOptionComponent>
    )
  }
}

export default withAnalytics(Duration)
